import { useState } from 'react';
import { FiLock, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { applyInviteCode } from '../utils/api';
import { useI18n } from '../i18n/useI18n';

/**
 * 邀请码输入弹窗组件
 *
 * 污点用户（未使用邀请码注册）登录后必须输入有效邀请码才能使用系统。
 * 该弹窗不可关闭，用户必须输入邀请码或登出。
 */
export function InviteCodeModal({ open, onSuccess }) {
  const { logout, clearTainted } = useAuth();
  const { t } = useI18n();
  const [inviteCode, setInviteCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const code = inviteCode.trim().toUpperCase();
    if (!code || code.length !== 6) {
      setError(t('inviteCodeModal.needCode'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await applyInviteCode(code);

      if (res.code === 0 && res.data?.success) {
        setSuccess(true);
        clearTainted();

        // 延迟关闭，让用户看到成功状态
        setTimeout(() => {
          onSuccess?.();
        }, 1500);
      } else {
        setError(res.data?.message || res.msg || t('inviteCodeModal.invalid'));
      }
    } catch (err) {
      setError(err.message || t('inviteCodeModal.verifyFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    // 只允许字母和数字，自动转大写，最多 6 位
    const value = e.target.value
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 6);
    setInviteCode(value);
    setError('');
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 bg-slate-950/90 backdrop-blur-xl flex items-center justify-center p-6 z-[200]"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-code-title"
    >
      <div className="glass-xl w-full max-w-[440px] p-8">
        {/* 标题区域 */}
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-4">
            {success ? (
              <FiCheck className="text-3xl text-emerald-400" />
            ) : (
              <FiLock className="text-3xl text-amber-400" />
            )}
          </div>
          <h2 id="invite-code-title" className="text-xl md:text-2xl font-bold text-white mb-2">
            {success ? t('inviteCodeModal.titleSuccess') : t('inviteCodeModal.titleInput')}
          </h2>
          <p className="text-slate-400 text-sm">
            {success ? t('inviteCodeModal.subtitleSuccess') : t('inviteCodeModal.subtitleInput')}
          </p>
        </div>

        {success ? (
          /* 成功状态 */
          <div className="flex flex-col items-center py-4">
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center animate-pulse">
              <FiCheck className="text-2xl text-emerald-400" />
            </div>
            <p className="text-slate-300 text-sm mt-4">{t('inviteCodeModal.redirecting')}</p>
          </div>
        ) : (
          /* 输入表单 */
          <form onSubmit={handleSubmit} className="flex flex-col gap-6">
            {/* 6位邀请码输入框 - 优化版 */}
            <div className="relative flex justify-center w-full">
              <input
                type="text"
                value={inviteCode}
                onChange={handleInputChange}
                maxLength={6}
                autoFocus
                autoComplete="off"
                className="absolute inset-0 w-full h-full opacity-0 cursor-text z-10 caret-transparent"
                aria-label={t('inviteCodeModal.ariaInput')}
                aria-invalid={!!error}
                aria-describedby={error ? 'invite-code-error' : undefined}
              />

              <div className="flex gap-2 md:gap-3">
                {[...Array(6)].map((_, index) => {
                  const isFilled = index < inviteCode.length;
                  const isActive = index === inviteCode.length;
                  const isError = !!error;

                  return (
                    <div
                      key={index}
                      className={`
                        w-11 h-14 md:w-14 md:h-16 
                        rounded-xl border 
                        flex items-center justify-center 
                        text-2xl md:text-3xl font-bold font-mono
                        transition-all duration-200
                        ${isFilled 
                          ? 'border-accent bg-accent/10 text-white shadow-[0_0_15px_rgba(14,165,233,0.15)]' 
                          : isActive
                            ? 'border-accent ring-2 ring-accent/20 bg-slate-800/80 text-white scale-105 z-0'
                            : 'border-slate-700 bg-slate-800/40 text-slate-500'
                        }
                        ${isError ? '!border-red-500/50 !bg-red-500/10 !text-red-200' : ''}
                      `}
                    >
                      {inviteCode[index] || (isActive && !loading ? <span className="w-0.5 h-6 bg-accent animate-blink"/> : '')}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 错误提示 */}
            {error && (
              <div
                id="invite-code-error"
                className="flex items-center gap-2 text-red-400 text-sm bg-red-500/10 px-4 py-3 rounded-lg"
                role="alert"
              >
                <FiAlertCircle className="flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* 提交按钮 */}
            <button
              type="submit"
              disabled={loading || inviteCode.length !== 6}
              className="w-full px-6 py-4 rounded-xl bg-accent hover:bg-accent/90 disabled:bg-slate-700 disabled:cursor-not-allowed text-white font-semibold text-base transition-all duration-200"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t('inviteCodeModal.verifying')}
                </span>
              ) : (
                t('inviteCodeModal.verifyButton')
              )}
            </button>
          </form>
        )}

        {/* 底部说明 */}
        {!success && (
          <div className="mt-8 pt-6 border-t border-slate-700/50 text-center">
            <p className="text-xs text-slate-500 mb-4">
              {t('inviteCodeModal.noCodeHint')}
            </p>
            <button
              onClick={logout}
              className="text-slate-400 hover:text-slate-200 text-sm transition-colors underline underline-offset-2"
            >
              {t('inviteCodeModal.otherAccount')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
